import React, { useEffect, useState, useRef } from "react";
import { array, number, bool, node, func, string } from "prop-types";
import { toBanglaNum } from "../../utils/utils";

import "./srollSnap.m.css";

const ScrollSnap = ({
  children,
  defaultSelection,
  isArrow,
  isIndicator,
  leftArrow,
  rightArrow,
  interval,
  isInfinite,
  displaySliderCount,
  selectionChange,
  pauseOnHover,
  lang,
  customStyle,
  src,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(defaultSelection ? defaultSelection - 1 : 0);
  const [autoScroll, setAutoScroll] = useState(false);

  const carouselStyle = customStyle ? "breakingnews-carousel carousel" : "carousel";
  const indicatorsStyle = customStyle ? "breakingnews-indicators indicators" : "indicators";
  const styleIndicator = customStyle ? "indicator-breakingnews indicator" : "indicator";
  const styleIndicatorButton = customStyle ? "breakingnews-indicators-btn indicator-button" : "indicator-button";
  const noOfItems = children ? children.length : 0;
  const scroller = useRef(null);

  useEffect(() => {
    if (scroller.current) {
      scroller.current.addEventListener(
        "scroll",
        debounce(() => {
          const index =
            src === "SixStoriesWithPoll"
              ? Math.ceil((scroller.current.scrollLeft / scroller.current.scrollWidth) * noOfItems) // for sixstorieswithpoll component will have to use the same ciel func for other components in future
              : Math.round((scroller.current.scrollLeft / scroller.current.scrollWidth) * noOfItems);
          setSelectedIndex(index);
        }, 150)
      );

      // For Default Selection if selectedIndex is not 0 then manually select it
      if (selectedIndex !== 0) {
        updateItemSelection(selectedIndex);
      }
    }

    return () => {
      scroller.current.removeEventListener("scroll", debounce);
    };
  }, []);

  useEffect(() => {
    if (selectedIndex !== defaultSelection) {
      updateItemSelection(defaultSelection);
    }
  }, [defaultSelection]);

  useEffect(() => {
    if (selectionChange) {
      selectionChange(selectedIndex);
    }
  }, [selectedIndex]);

  // Auto Scroll
  useEffect(() => {
    if (isInfinite && !autoScroll) {
      const timer = setInterval(() => {
        setSelectedIndex((value) => {
          const newIndex = value === noOfItems - 1 ? 0 : value + 1;

          updateItemSelection(newIndex);

          return newIndex;
        });
      }, interval);

      return () => {
        clearInterval(timer);
      };
    }
  }, [autoScroll]);

  const debounce = (func, ms) => {
    let timeout;

    return () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        timeout = null;
        func();
      }, ms);
    };
  };

  const smoothScroll = (node, topOrLeft, horizontal) => {
    return node.scrollTo({
      [horizontal ? "left" : "top"]: topOrLeft,
      behavior: "smooth",
    });
  };

  const indicatorClick = (e, newIndex) => {
    e.preventDefault();
    e.stopPropagation();

    if (scroller.current) {
      setSelectedIndex(newIndex);
      updateItemSelection(newIndex);
    }
  };

  const nextClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (scroller.current) {
      if (selectedIndex < noOfItems) {
        const newIndex = selectedIndex + 1;
        setSelectedIndex(newIndex);
        updateItemSelection(newIndex);
      }
    }
  };

  const previousClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (scroller.current) {
      const newIndex = selectedIndex - 1;
      setSelectedIndex(newIndex);
      updateItemSelection(newIndex);
    }
  };

  const updateItemSelection = (index) => {
    const scrollLeft = Math.floor(scroller.current.scrollWidth * (index / noOfItems));
    smoothScroll(scroller.current, scrollLeft, true);
  };

  const arrowView = () => (
    <React.Fragment>
      {/* Left Arrow Button */}
      {selectedIndex !== 0 ? (
        <span onClick={previousClick} styleName="left-arrow" className="left-arrow">
          {leftArrow}
        </span>
      ) : null}

      {/* Right Arrow Button */}
      {selectedIndex < noOfItems - 1 && customArrowView() ? (
        <span onClick={nextClick} styleName="right-arrow" className="right-arrow">
          {rightArrow}
        </span>
      ) : null}
    </React.Fragment>
  );

  const pauseAutoPlay = () => {
    setAutoScroll(!autoScroll);
  };

  const customArrowView = () => {
    if (src === "SliderFocusedCard") {
      if (global.isDesktop && selectedIndex >= children.length - 4) {
        return false;
      } else if (global.isTab && selectedIndex >= children.length - 3) {
        return false;
      } else return true;
    }
    return true;
  };

  return (
    <>
      <div
        styleName="wrapper"
        className="scroll-snap-wrapper"
        onMouseEnter={pauseOnHover ? () => pauseAutoPlay() : null}
        onMouseLeave={pauseOnHover ? () => pauseAutoPlay() : null}
      >
        <div ref={scroller} styleName={carouselStyle} className="scroll-snap-carousel">
          {children}
        </div>
        {displaySliderCount && (
          <div styleName="slider-count">{`${lang === "en" ? selectedIndex + 1 : toBanglaNum(selectedIndex + 1)}/${
            lang === "en" ? children.length : toBanglaNum(children.length)
          }`}</div>
        )}
        {isArrow ? arrowView() : null}
      </div>

      {isIndicator ? (
        <ul styleName={indicatorsStyle}>
          {[...Array(noOfItems).keys()].map((index) => (
            <li key={index} styleName={styleIndicator} className="indicator" onClick={(e) => indicatorClick(e, index)}>
              <button
                styleName={styleIndicatorButton}
                className="indicator-button"
                aria-label="indicator button"
                aria-pressed={selectedIndex === index ? "true" : "false"}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
};

ScrollSnap.defaultProps = {
  isArrow: true,
  isIndicator: true,
  defaultSelection: 0,
  isInfinite: false,
  interval: 5000,
  displaySliderCount: false,
  pauseOnHover: false,
  customStyle: false,
  src: "",
};

ScrollSnap.propTypes = {
  children: array.isRequired,
  leftArrow: node.isRequired,
  rightArrow: node.isRequired,
  defaultSelection: number,
  isArrow: bool,
  isIndicator: bool,
  isInfinite: bool,
  interval: number,
  displaySliderCount: bool,
  selectionChange: func,
  lang: string,
  pauseOnHover: bool,
  customStyle: bool,
  src: string,
};

export default ScrollSnap;
